import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useState } from 'react';
import {
  getUserMediaErrorInfo,
  type MediaErrorInfo,
} from './get-user-media-error-info';

export interface MediaDeviceInfo {
  deviceId: string;
  label: string;
  kind: 'videoinput' | 'audioinput';
}

/**
 * Synthetic error info used when enumeration itself fails rather than
 * `getUserMedia`. Kept here so the UI only has to handle one error shape.
 */
const ENUMERATION_ERROR: MediaErrorInfo = {
  name: 'EnumerationError',
  title: 'Could not list your devices',
  message:
    'We were unable to list the cameras and microphones connected to this device.',
  advice: [
    'Reload the page and try again.',
    'If the problem continues, restart your browser.',
  ],
  recoverable: true,
};

const UNSUPPORTED_ERROR: MediaErrorInfo = {
  name: 'UnsupportedError',
  title: 'Your browser does not support media devices',
  message:
    'Media device enumeration is not supported in this browser. Please use a modern version of Chrome, Edge, Firefox or Safari.',
  advice: ['Open this page in a supported browser.'],
  recoverable: false,
};

interface MediaDevicesHook {
  /**
   * Available media devices, and which ones are selected.
   */
  videoDevices: MediaDeviceInfo[];
  audioDevices: MediaDeviceInfo[];
  selectedVideoDevice: string | null;
  selectedAudioDevice: string | null;

  // Device selection actions
  selectVideoDevice: (deviceId: string) => void;
  selectAudioDevice: (deviceId: string) => void;

  /**
   * Prompts (if necessary) and enumerates available media devices.
   */
  enumerateDevices: () => Promise<void>;

  /**
   * Status flags
   */
  isEnumerating: boolean;
  hasDevicePermission: boolean;
  deviceError: MediaErrorInfo | null;

  // Device constraints
  getDeviceConstraints: (
    overrideVideoDevice?: string,
    overrideAudioDevice?: string,
  ) => MediaStreamConstraints;
}

/**
 * Hook to manage media device enumeration and selection for WebRTC calls.
 * Handles camera and microphone device discovery, selection, and permission management.
 *
 * @param isActive - Whether the hook should be active (to prevent media devices being used
 * when the component is mounted, but not active, i.e. camera off if not being displayed!)
 */
export function useMediaDevices(isActive: boolean = true): MediaDevicesHook {
  // Media
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<string | null>(null); // prettier-ignore
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<string | null>(null); // prettier-ignore

  // Status Flags
  const [isEnumerating, setIsEnumerating] = useState(false);
  const [hasDevicePermission, setHasDevicePermission] = useState(false);
  const [deviceError, setDeviceError] = useState<MediaErrorInfo | null>(null);

  /**
   * Check if we have permission to enumerate devices with labels
   */
  const checkDevicePermission = useCallback(async (): Promise<boolean> => {
    try {
      // Try to get user media to trigger permission
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });

      // Stop the stream immediately - we just needed it for permission
      stream.getTracks().forEach((track) => track.stop());

      setHasDevicePermission(true);
      setDeviceError(null);
      return true;
    } catch (error) {
      console.error(
        'Media Devices: Permission denied or no devices found:',
        error,
      );
      Sentry.withScope((scope) => {
        scope.setTag('feature', 'video-calling');
        scope.setTag('component', 'use-media-devices');
        if (error instanceof Error) {
          scope.setTag('device_error_name', error.name);
        }
        Sentry.captureException(error);
      });
      setHasDevicePermission(false);
      setDeviceError(getUserMediaErrorInfo(error));
      return false;
    }
  }, []);

  /**
   * Enumerate available media devices
   *
   * Docs: https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/enumerateDevices
   */
  const enumerateDevices = useCallback(async (): Promise<void> => {
    if (!isActive) {
      return;
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      setDeviceError(UNSUPPORTED_ERROR);
      return;
    }

    setIsEnumerating(true);
    setDeviceError(null);

    try {
      const hasPermission = await checkDevicePermission();
      if (!hasPermission) {
        setIsEnumerating(false);
        return;
      }

      // Get all devices
      const devices = await navigator.mediaDevices.enumerateDevices();

      // Filter and format devices
      const videoInputs: MediaDeviceInfo[] = [];
      const audioInputs: MediaDeviceInfo[] = [];

      devices.forEach((device) => {
        if (device.kind === 'videoinput') {
          videoInputs.push({
            deviceId: device.deviceId,
            label: device.label || `Camera ${videoInputs.length + 1}`,
            kind: 'videoinput',
          });
        } else if (device.kind === 'audioinput') {
          audioInputs.push({
            deviceId: device.deviceId,
            label: device.label || `Microphone ${audioInputs.length + 1}`,
            kind: 'audioinput',
          });
        }
      });

      setVideoDevices(videoInputs);
      setAudioDevices(audioInputs);

      // Auto-select first devices if none selected
      if (videoInputs.length > 0 && !selectedVideoDevice) {
        setSelectedVideoDevice(videoInputs[0].deviceId);
      }
      if (audioInputs.length > 0 && !selectedAudioDevice) {
        setSelectedAudioDevice(audioInputs[0].deviceId);
      }
    } catch (error) {
      console.error('Media Devices: Error enumerating devices:', error);
      Sentry.withScope((scope) => {
        scope.setTag('feature', 'video-calling');
        scope.setTag('component', 'use-media-devices');
        Sentry.captureException(error);
      });
      setDeviceError(ENUMERATION_ERROR);
    } finally {
      setIsEnumerating(false);
    }
  }, [
    isActive,
    checkDevicePermission,
    selectedVideoDevice,
    selectedAudioDevice,
  ]);

  /**
   * Select a video device
   */
  const selectVideoDevice = useCallback(
    (deviceId: string) => {
      const device = videoDevices.find((d) => d.deviceId === deviceId);
      if (device) {
        setSelectedVideoDevice(deviceId);
      }
    },
    [videoDevices],
  );

  /**
   * Select an audio device
   */
  const selectAudioDevice = useCallback(
    (deviceId: string) => {
      const device = audioDevices.find((d) => d.deviceId === deviceId);
      if (device) {
        setSelectedAudioDevice(deviceId);
      }
    },
    [audioDevices],
  );

  /**
   * Listen for device changes (devices plugged/unplugged) only when active
   */
  useEffect(() => {
    if (
      !isActive ||
      !navigator.mediaDevices ||
      !navigator.mediaDevices.addEventListener
    ) {
      return;
    }

    /**
     * When the devices change, re-enumerate them
     */
    const handleDeviceChange = () => {
      enumerateDevices();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    // Clean up the device change listener
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange); // prettier-ignore
    };
  }, [isActive, enumerateDevices]);

  /**
   * Handle active state changes - cleanup only, no auto-enumeration
   */
  useEffect(() => {
    if (!isActive) {
      setVideoDevices([]);
      setAudioDevices([]);
      setSelectedVideoDevice(null);
      setSelectedAudioDevice(null);
      setIsEnumerating(false);
      setHasDevicePermission(false);
      setDeviceError(null);
    }
  }, [isActive]);

  /**
   * Get device constraints for getUserMedia
   */
  const getDeviceConstraints = useCallback(
    (overrideVideoDevice?: string, overrideAudioDevice?: string) => {
      const finalVideoDevice = overrideVideoDevice || selectedVideoDevice;
      const finalAudioDevice = overrideAudioDevice || selectedAudioDevice;

      const constraints: MediaStreamConstraints = {
        video: finalVideoDevice
          ? { deviceId: { exact: finalVideoDevice } }
          : true,
        audio: finalAudioDevice
          ? { deviceId: { exact: finalAudioDevice } }
          : true,
      };

      return constraints;
    },
    [selectedVideoDevice, selectedAudioDevice],
  );

  return {
    // Available devices
    videoDevices,
    audioDevices,

    // Selected devices
    selectedVideoDevice,
    selectedAudioDevice,

    // Device selection actions
    selectVideoDevice,
    selectAudioDevice,

    // Device enumeration
    enumerateDevices,

    // Status
    isEnumerating,
    hasDevicePermission,
    deviceError,

    // Device constraints
    getDeviceConstraints,
  };
}
